import {
	SportObjectEntity,
	GetAllSportObjectsDocument,
	CreateSportObjectDocument,
	CreateSportObjectInput,
	DeleteSportObjectDocument,
	UpdateSportObjectInput,
	UpdateSportObjectDocument,
	SportZoneEntity,
	SportSubZoneEntity,
	CreateSportZoneInput,
	CreateSportZoneDocument,
	UpdateSportZoneInput,
	UpdateSportZoneDocument,
	DeleteSportZoneDocument,
	CreateSportSubZoneInput,
	CreateSportSubZoneDocument,
	UpdateSportSubZoneInput,
	UpdateSportSubZoneDocument, DeleteSportSubZoneDocument, SportEquipmentEntity
} from '../../graphql/graphQlApiHooks';
import {makeAutoObservable, runInAction, autorun} from "mobx";
import RootStore, {client} from "../.";
import {sortByCreateAt, queryToMobxObservable} from '../../helpers';
import Settings from "../settings";

const settingsStore = class {
	public rootStore?: RootStore;

	public sportObjects: SportObjectEntity[] = [];
	public sportObject?: SportObjectEntity;

	public sportZones: SportZoneEntity[] = [];
	public sportZone?: SportZoneEntity;

	public sportSubZones: SportSubZoneEntity[] = [];
	public sportSubZone?: SportSubZoneEntity;

	public sportObjectsCount: number = 0
	public sportZonesCount: number = 0
	public sportSubZonesCount: number = 0
	public loading: boolean = true
	private queryLoading: boolean = true
	public isRefetching: boolean = false

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
		makeAutoObservable(this)
		
		// Загружаем все объекты для настроек независимо от доступности пользователю
		autorun(() => {
			if (this.queryAllSportObjects) {
				const query = this.queryAllSportObjects;
				const queryResult = query.current();
				
				// Если есть результат, значит загрузка завершена
				if (queryResult) {
					let result: any = queryResult
					result = result.getAllSportObjects.slice().sort(sortByCreateAt);
					
					// Обновляем объекты только если они действительно изменились
					if (JSON.stringify(result) !== JSON.stringify(this.sportObjects)) {
						this.setSportObjects(result);
					}
					
					// Устанавливаем загрузку в false после получения данных
					if (this.queryLoading) {
						runInAction(() => {
							this.queryLoading = false;
							this.loading = false;
						});
					}
				} else {
					// Если нет результата, но запрос существует, значит идет загрузка
					if (!this.queryLoading) {
						runInAction(() => {
							this.queryLoading = true;
							this.loading = true;
						});
					}
				}
			}
		})
	}

	public setSportObject = (sportObjectId: string) => {
		const current = this.sportObjects.find((obj: SportObjectEntity) => obj.id === sportObjectId)
		if (current && current.id !== this.sportObject?.id) {
			runInAction(() => this.sportObject = current);
			if (current?.sportZones) {
				this.setSportZones(current.sportZones)
			}
		}
	};

	public setSportObjects = (sportObjects: SportObjectEntity[]) => {
		if (JSON.stringify(sportObjects) === JSON.stringify(this.sportObjects)) return
		
		runInAction(() => {
			this.sportObjects = sportObjects;
			this.sportObjectsCount = sportObjects.length;
		})

		// Устанавливаем первый объект только если нет выбранного объекта
		if (!this.sportObject && sportObjects.length > 0) {
			this.setSportObject(sportObjects[0].id)
		}
	};

	public setSportZone = (sportZoneId: string) => {
		const sportZone = this.sportZones.find((zone: SportZoneEntity) => {
			return zone.id === sportZoneId
		})
		this.sportZone = sportZone
		if (sportZone && sportZone?.sportSubZones) {
			this.setSportSubZones(sportZone.sportSubZones)
		} else {
			this.sportSubZones = []
			this.sportSubZone = undefined
		}
	};

	public setSportZones = (sportZones: SportZoneEntity[]) => {
		
		this.sportZones = sportZones.slice().sort(sortByCreateAt);
		this.sportZonesCount = sportZones.length;
		if (sportZones.length > 0) {
			this.setSportZone(sportZones[0].id)
			if (sportZones[0]?.sportSubZones && sportZones[0].sportSubZones.length > 0) {
				this.setSportSubZones(sportZones[0].sportSubZones)
			}
		} else {
			this.sportZones = []
			this.sportZone = undefined
			this.sportSubZones = []
			this.sportSubZone = undefined
		}
	};

	public setSportSubZone = (sportSubZoneId: string) => {
		this.sportSubZone = this.sportSubZones.find((zone: SportSubZoneEntity) => {
			return zone.id === sportSubZoneId
		});
	};

	public setSportSubZones = (sportSubZones: SportSubZoneEntity[]) => {
		this.sportSubZones = sportSubZones.slice().sort(sortByCreateAt);
		this.sportSubZonesCount = sportSubZones.length;
		if (sportSubZones.length > 0) {
			this.setSportSubZone(sportSubZones[0].id);
		} else {
			this.sportSubZones = [];
			this.sportSubZone = undefined;
		}
	};

	public hasEquipments = () => {
		const equipmentsBySportObjectId = this.rootStore?.equipmentsStore.equipmentsResponse.filter((equipment: SportEquipmentEntity) => {
			return equipment.sportObject.id === this.sportObject?.id
		})

		return Boolean(equipmentsBySportObjectId.length > 0)
	}

	sportObjectUpdate = (input: UpdateSportObjectInput) => client.mutate({
		mutation: UpdateSportObjectDocument,
		variables: {input},
		refetchQueries: [{query: GetAllSportObjectsDocument, fetchPolicy: 'network-only'}]
	})
			.then(async () => {
				this.rootStore?.setSnakeMessage('Спортивный объект успешно обнавлён.')
				// Принудительно обновляем список объектов в настройках
				await this.refetchSettingsData();
				
				// Обновляем текущий выбранный объект с новыми данными
				if (input.id) {
					const updatedObject = this.sportObjects.find(obj => obj.id === input.id);
					if (updatedObject) {
						runInAction(() => {
							this.sportObject = updatedObject;
						});
					}
				}
			})
			.catch((error: any) => this.rootStore?.setSnakeMessage(error.message, 'error'));

	sportObjectCreate = (input: CreateSportObjectInput) => client.mutate({
		mutation: CreateSportObjectDocument,
		variables: {input},
		refetchQueries: [{query: GetAllSportObjectsDocument, fetchPolicy: 'network-only'}]
	})
			.then(async (res) => {
				this.rootStore?.setSnakeMessage('Новый спортивный объект успешно создан.');
				// Принудительно обновляем список объектов в настройках
				await this.refetchSettingsData();
				// Возвращаем созданный объект
				return res.data?.createSportObject;
			})
			.catch((error: any) => {
				this.rootStore?.setSnakeMessage(error.message, 'error');
				throw error;
			});

	sportObjectDelete = (id: string) => client.mutate({
		mutation: DeleteSportObjectDocument,
		variables: {id},
		refetchQueries: [{query: GetAllSportObjectsDocument, fetchPolicy: 'network-only'}]
	})
			.then(async () => {
				this.rootStore?.setSnakeMessage('Спортивный объект успешно удалён.')
				runInAction(() => {
					this.sportObject = undefined
				})
				// Принудительно обновляем список объектов в настройках
				await this.refetchSettingsData();
			})
			.catch((error: any) => this.rootStore?.setSnakeMessage(error.message, 'error'));


	sportZoneUpdate = (input: UpdateSportZoneInput) => {
		
		return client.mutate({
			mutation: UpdateSportZoneDocument,
			variables: {input},
			refetchQueries: [{query: GetAllSportObjectsDocument, fetchPolicy: 'network-only'}]
		})
				.then(async () => {
					
					this.rootStore?.setSnakeMessage('Здание/Помещение успешно обновлено.')
					// Принудительно обновляем список объектов в настройках
					await this.refetchSettingsData();
					
					// Обновляем sportZones для текущего спортивного объекта
					if (this.sportObject) {
						const updatedSportObject = this.sportObjects.find(obj => obj.id === this.sportObject?.id);
						if (updatedSportObject?.sportZones) {
							this.setSportZones(updatedSportObject.sportZones);
						}
					}
				})
				.catch((error: any) => {
					console.error('❌ Ошибка при редактировании спортивной зоны:', {
						error: error.message,
						input,
						currentSportObject: this.sportObject?.name
					});
					this.rootStore?.setSnakeMessage(error.message, 'error');
				});
	};

	sportZoneCreate = (input: CreateSportZoneInput) => {
		
		return client.mutate({
			mutation: CreateSportZoneDocument,
			variables: {input},
			refetchQueries: [{query: GetAllSportObjectsDocument, fetchPolicy: 'network-only'}]
		})
				.then(async (res) => {
					
					this.rootStore?.setSnakeMessage('Новое здание/помещение успешно создано.');
					// Принудительно обновляем список объектов в настройках
					await this.refetchSettingsData();
					
					// Обновляем sportZones для текущего спортивного объекта
					if (this.sportObject) {
						const updatedSportObject = this.sportObjects.find(obj => obj.id === this.sportObject?.id);
						if (updatedSportObject?.sportZones) {
							this.setSportZones(updatedSportObject.sportZones);
						}
					}
					
					// Возвращаем созданную зону
					return res.data?.createSportZone;
				})
				.catch((error: any) => {
					console.error('❌ Ошибка при создании спортивной зоны:', {
						error: error.message,
						input,
						currentSportObject: this.sportObject?.name
					});
					this.rootStore?.setSnakeMessage(error.message, 'error');
					throw error;
				});
	};

	sportZoneDelete = (id: string) => {
		const zoneToDelete = this.sportZones.find(z => z.id === id);
		
		return client.mutate({
			mutation: DeleteSportZoneDocument,
			variables: {id},
			refetchQueries: [{query: GetAllSportObjectsDocument, fetchPolicy: 'network-only'}]
		})
				.then(async () => {
					
					this.rootStore?.setSnakeMessage('Здание/помещение успешно удалено.');
					// Принудительно обновляем список объектов в настройках
					await this.refetchSettingsData();
					
					// Обновляем sportZones для текущего спортивного объекта
					if (this.sportObject) {
						const updatedSportObject = this.sportObjects.find(obj => obj.id === this.sportObject?.id);
						if (updatedSportObject?.sportZones) {
							this.setSportZones(updatedSportObject.sportZones);
						} else {
							// Если у объекта нет зон, очищаем текущие зоны
							runInAction(() => {
								this.sportZones = [];
								this.sportZone = undefined;
								this.sportSubZones = [];
								this.sportSubZone = undefined;
							});
						}
					}
				})
				.catch((error: any) => {
					console.error('❌ Ошибка при удалении спортивной зоны:', {
						error: error.message,
						zoneId: id,
						zoneName: zoneToDelete?.name,
						currentSportObject: this.sportObject?.name
					});
					this.rootStore?.setSnakeMessage(error.message, 'error');
				});
	};


	sportSubZoneUpdate = (input: UpdateSportSubZoneInput) => {
		return client.mutate({
			mutation: UpdateSportSubZoneDocument,
			variables: {input},
			refetchQueries: [{query: GetAllSportObjectsDocument, fetchPolicy: 'network-only'}]
		})
				.then(async () => {
					this.rootStore?.setSnakeMessage('Подзона спорта успешно обновлена.')
					// Принудительно обновляем список объектов в настройках
					await this.refetchSettingsData();
					
					// Обновляем sportSubZones для текущей спортивной зоны
					if (this.sportZone) {
						const updatedSportObject = this.sportObjects.find(obj => obj.id === this.sportObject?.id);
						const updatedSportZone = updatedSportObject?.sportZones?.find(zone => zone.id === this.sportZone?.id);
						if (updatedSportZone?.sportSubZones) {
							this.setSportSubZones(updatedSportZone.sportSubZones);
						}
					}
				})
				.catch((error: any) => {
					console.error('❌ Ошибка при редактировании подзоны спорта:', {
						error: error.message,
						input,
						currentSportZone: this.sportZone?.name
					});
					this.rootStore?.setSnakeMessage(error.message, 'error');
				});
	};

	sportSubZoneCreate = (input: CreateSportSubZoneInput) => {
		return client.mutate({
			mutation: CreateSportSubZoneDocument,
			variables: {input},
			refetchQueries: [{query: GetAllSportObjectsDocument, fetchPolicy: 'network-only'}]
		})
				.then(async (res) => {
					this.rootStore?.setSnakeMessage('Новая подзона спорта успешно создана.');
					// Принудительно обновляем список объектов в настройках
					await this.refetchSettingsData();
					
					// Обновляем sportSubZones для текущей спортивной зоны
					if (this.sportZone) {
						const updatedSportObject = this.sportObjects.find(obj => obj.id === this.sportObject?.id);
						const updatedSportZone = updatedSportObject?.sportZones?.find(zone => zone.id === this.sportZone?.id);
						if (updatedSportZone?.sportSubZones) {
							this.setSportSubZones(updatedSportZone.sportSubZones);
						}
					}
					
					// Возвращаем созданную подзону
					return res.data?.createSportSubZone;
				})
				.catch((error: any) => {
					console.error('❌ Ошибка при создании подзоны спорта:', {
						error: error.message,
						input,
						currentSportZone: this.sportZone?.name
					});
					this.rootStore?.setSnakeMessage(error.message, 'error');
				});
	};

	sportSubZoneDelete = (id: string) => {
		const subZoneToDelete = this.sportSubZones.find(z => z.id === id);
		
		return client.mutate({
			mutation: DeleteSportSubZoneDocument,
			variables: {id},
			refetchQueries: [{query: GetAllSportObjectsDocument, fetchPolicy: 'network-only'}]
		})
				.then(async () => {
					this.rootStore?.setSnakeMessage('Подзона спорта успешно удалена.');
					// Принудительно обновляем список объектов в настройках
					await this.refetchSettingsData();
					
					// Обновляем sportSubZones для текущей спортивной зоны
					if (this.sportZone) {
						const updatedSportObject = this.sportObjects.find(obj => obj.id === this.sportObject?.id);
						const updatedSportZone = updatedSportObject?.sportZones?.find(zone => zone.id === this.sportZone?.id);
						if (updatedSportZone?.sportSubZones) {
							this.setSportSubZones(updatedSportZone.sportSubZones);
						} else {
							// Если у зоны нет подзон, очищаем текущие подзоны
							runInAction(() => {
								this.sportSubZones = [];
								this.sportSubZone = undefined;
							});
						}
					}
				})
				.catch((error: any) => {
					console.error('❌ Ошибка при удалении подзоны спорта:', {
						error: error.message,
						subZoneId: id,
						subZoneName: subZoneToDelete?.name,
						currentSportZone: this.sportZone?.name
					});
					this.rootStore?.setSnakeMessage(error.message, 'error');
				});
	};

	// Геттер для загрузки всех объектов в настройках
	get queryAllSportObjects() {
		if (this.rootStore?.userStore?.currentUser) {
			return queryToMobxObservable(client.watchQuery({
				query: GetAllSportObjectsDocument,
				pollInterval: Settings.pollIntervals.allSportObjects,
			}));
		}
		return undefined;
	}

	// Метод для принудительного обновления данных в настройках
	public refetchSettingsData = async () => {
		if (this.queryAllSportObjects) {
			// Устанавливаем флаг обновления
			runInAction(() => {
				this.isRefetching = true;
			});
			
			try {
				// Принудительно обновляем данные через GraphQL без показа загрузки
				await client.query({
					query: GetAllSportObjectsDocument,
					fetchPolicy: 'network-only'
				});
			} catch (error) {
				console.error('❌ Ошибка при обновлении данных настроек:', error);
			} finally {
				// Убираем флаг обновления
				runInAction(() => {
					this.isRefetching = false;
				});
			}
		}
	}
};


export default settingsStore;
